// OBT F3-C — Genel bakış: kapsam-geneli kampanya durumu + cevap oranı + grafik.
import { useMemo } from 'react';
import { Megaphone, PlayCircle, CheckCircle2, Percent, Clock } from 'lucide-react';
import { ObtLoading, ObtErrorState, ObtEmptyState, ObtStatTile } from '../ObtStates';
import { CAMPAIGN_STATUS_LABELS, obtLabel } from '../obtEnums';
import ReportChart from './ReportChart';
import { useOrgOverview } from './hooks/useOrgOverview';
import {
  campaignStatusCounts,
  sumByStatuses,
  aggregateResponseRate,
  responseRate,
  hasOverdue,
  ONGOING_CAMPAIGN_STATUSES,
  COMPLETED_CAMPAIGN_STATUSES,
} from './reportMetrics';
import { formatPercent } from './reportFormat';

export default function OverviewPanel({ enabled = true }) {
  const { loading, error, items, total, hasMore, sampleLimit, reload } = useOrgOverview({ enabled });

  const counts = useMemo(() => campaignStatusCounts(items), [items]);
  const ongoing = sumByStatuses(counts, ONGOING_CAMPAIGN_STATUSES);
  const completed = sumByStatuses(counts, COMPLETED_CAMPAIGN_STATUSES);
  const published = Number(counts.published || 0) + Number(counts.collecting_responses || 0);
  const overdueCampaigns = useMemo(() => items.filter(hasOverdue).length, [items]);
  const respRate = useMemo(() => aggregateResponseRate(items), [items]);

  const statusChartData = useMemo(
    () =>
      Object.entries(counts).map(([status, value]) => ({
        name: obtLabel(CAMPAIGN_STATUS_LABELS, status),
        value,
      })),
    [counts]
  );

  // Kampanya bazlı cevap oranı (yüzde) — en fazla 12 kampanya gösterilir.
  const rateChartData = useMemo(
    () =>
      items
        .map((c) => ({ name: c.title || 'Bilgi Talebi', rate: responseRate(c) }))
        .filter((d) => d.rate !== null)
        .slice(0, 12)
        .map((d) => ({ name: d.name, value: Math.round(d.rate * 100) })),
    [items]
  );

  if (loading) return <ObtLoading label="Genel bakış yükleniyor…" />;
  if (error) return <ObtErrorState error={error} onRetry={reload} />;
  if (total === 0) {
    return (
      <ObtEmptyState
        title="Kapsamınızda bilgi talebi yok"
        description="Raporlanacak bir bilgi talebi bulunmuyor."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <ObtStatTile label="Toplam bilgi talebi" value={total} icon={Megaphone} tone="info" />
        <ObtStatTile label="Yayında (cevap toplanıyor)" value={published} icon={PlayCircle} tone="info" />
        <ObtStatTile label="Devam eden" value={ongoing} icon={PlayCircle} tone="warning" />
        <ObtStatTile label="Tamamlanan" value={completed} icon={CheckCircle2} tone="success" />
        <ObtStatTile
          label="Cevap oranı"
          value={formatPercent(respRate)}
          icon={Percent}
          tone="neutral"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <ObtStatTile label="Süresi geçen bilgi talebi" value={overdueCampaigns} icon={Clock} tone="danger" />
      </div>

      {hasMore && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-inset ring-amber-200">
          Kapsamınızda {total} bilgi talebi var; genel bakış ilk {sampleLimit} bilgi talebi üzerinden
          hesaplanmıştır (backend kapsam-geneli özet ucu sağlamadığından). Bilgi talebi bazlı tam liste
          için "Bilgi Talepleri" sekmesini kullanın.
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <ReportChart
          title="Bilgi talebi durum dağılımı"
          data={statusChartData}
          valueFormatter={(v) => `${v} bilgi talebi`}
        />
        <ReportChart
          title="Bilgi talebi bazlı cevap oranı (%)"
          data={rateChartData}
          valueFormatter={(v) => `%${v}`}
          emptyText="Cevap oranı için veri yok."
        />
      </div>
    </div>
  );
}
